// Common types for the application
import {
  UserRole,
  PermissionResource,
  PermissionAction,
  UserManagementError,
  JobDefinitionError as JobDefinitionErrorEnum,
} from './enums'

export interface User {
  id: string
  email: string
  name?: string
  nickname?: string
  profile?: string
  avatar?: string
  token?: string
  roles?: string[]
  // Extended for user management
  role?: UserRole
  cognito_groups?: string[]
  permissions?: PermissionMatrix
  status?: 'active' | 'inactive' | 'pending_confirmation'
  created_at?: string
  last_login?: string
}

export interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  userPermissions: UserPermissions | null
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface AuthResponse {
  isSignedIn: boolean
  nextStep?: {
    signInStep: string
  }
}

export interface AuthContextType extends AuthState {
  signIn: (credentials: LoginCredentials) => Promise<AuthResponse>
  signOut: () => Promise<void>
  getCurrentUser: () => Promise<User | null>
  clearError: () => void
  // Extended for role-based access control
  userPermissions: UserPermissions | null
  hasPermission: (
    resource: PermissionResource,
    action: PermissionAction
  ) => boolean
  hasRole: (role: UserRole) => boolean
  refreshPermissions: () => Promise<void>
}

// API Response types
export interface ApiResponse<T = unknown> {
  data: T
  success?: boolean
  message?: string
}

export interface ApiError {
  message: string
  status: number
  code?: string
  details?: unknown
}

export interface PaginationParams {
  size?: number
  start?: string
  estado?: string
  processing_status?: string
  sort_field?: string
  sort_order?: string
  qualification_status?: string
}

export interface PaginatedResponse<T> {
  items: T[]
  total_items: number
  next_key?: string
  page_size: number
}

// Job posting (Solicitud) types
export interface SolicitudListItem {
  id_solicitud: string
  fecha_creacion: string
  estado: 'active' | 'inicio_postulacion' | 'fin_postulacion'
  approval_status?: 'pending' | 'approved' | 'rejected'
  total_postulaciones?: number
  total_evaluadas?: number
  // Requirement-related fields
  requerimiento?: {
    puesto_solicitado: string
    custom_fields?: Record<string, any>
    created_at?: string
    created_by?: string
  }
  requirement_status?: 'not_started' | 'pending' | 'approved' | 'rejected'
  job_definition_completed?: boolean
  // Job definition fields
  job_definition_id?: string
  job_definition_name?: string
  job_definition_puesto?: string
  jd?: {
    estructura?: {
      puesto_solicitado?: {
        puesto: string
      }
    }
  }
}

export interface SolicitudesResponse {
  data: {
    items: SolicitudListItem[]
    total_items: number
    next_key?: string
    page_size: number
  }
}

export interface SolicitudPayload {
  tipo: 'jd'
  job_definition_id?: string // NEW: Optional job definition ID for predefined job definitions
  estructura: {
    puesto_solicitado: {
      puesto: string
    }
  }
  perfil: {
    idiomas: Array<{
      lenguaje: string
      nivel: string
    }>
    experiencia_puesto: {
      anios: number
      meses: number
    }
    experiencia_area: {
      anios: number
      meses: number
    }
    estudios: Array<{
      nivel: string
      detalle: string
    }>
    conocimientos: string[]
  }
  requisitos: {
    indispensables: string[]
  }
  funciones: Array<{
    funcion: string
    ocupabilidad: number
  }>
  solicitante: {
    nombre: string
    correo: string
    cargo: string
  }
  fecha_creacion: string
}

export interface SolicitudResponse {
  id_solicitud: string
  estado: 'active'
  fecha_creacion: string
  redirect_url: string
  message: string
}

export interface SolicitudDetailResponse {
  data: {
    id_solicitud: string
    estado: string
    fecha_creacion: string
    // Job definition fields
    job_definition_id?: string
    job_definition_name?: string
    job_definition_puesto?: string
    job_definition?: {
      id: string
      name: string
      puesto: string
      funciones?: Array<{
        funcion: string
        ocupabilidad: number
      }>
      perfil?: {
        idiomas?: Array<{
          lenguaje: string
          nivel: string
        }>
        experiencia_puesto?: {
          anios: number
          meses: number
        }
        experiencia_area?: {
          anios: number
          meses: number
        }
        estudios?: Array<{
          nivel: string
          detalle: string
        }>
        conocimientos?: string[]
      }
      requisitos?: {
        indispensables?: string[]
      }
    }
    // Requirement fields
    requerimiento?: {
      puesto_solicitado: string
      custom_fields?: Record<string, any>
      created_at?: string
      created_by?: string
    }
    jd?: {
      estructura?: {
        puesto_solicitado?: {
          puesto: string
        }
      }
      perfil?: {
        idiomas?: Array<{
          lenguaje: string
          nivel: string
        }>
        experiencia_puesto?: {
          anios: number
          meses: number
        }
        experiencia_area?: {
          anios: number
          meses: number
        }
        estudios?: Array<{
          nivel: string
          detalle: string
        }>
        conocimientos?: string[]
      }
      requisitos?: {
        indispensables?: string[]
      }
      funciones?: Array<{
        funcion: string
        ocupabilidad: number
      }>
      solicitante?: {
        nombre: string
        correo: string
        cargo: string
      }
    }
  }
}

// Candidate (Postulacion) types
export interface ProcessingInfo {
  status: 'uploading' | 'processing' | 'completed' | 'error'
  status_display: string
}

export interface ExtractedInfo {
  nombres_extracted?: string
  correo_extracted?: string
  telefono_extracted?: string
  cargo_actual_extracted?: string
  extraction_status: 'success' | 'partial' | 'error'
  extraction_errors: string[]
}

export interface AIEvaluationItem {
  requisito: string
  puntaje: number
  total: number
  motivo: string
}

export interface AIEvaluationResult {
  evaluacion: AIEvaluationItem[]
  puntaje_total: number
  puntaje_obtenido: number
  fecha_evaluacion: string
  estado_evaluacion: 'completed' | 'processing' | 'error' | 'pending'
  confianza?: number
  observaciones?: string
}

export interface CVInfo {
  filename?: string
  has_cv: boolean
  upload_date?: string
}

export interface ProcessingSummary {
  uploading: number
  processing: number
  completed: number
  error: number
  total: number
}

export interface Candidate {
  id_postulacion: string
  nombres: string
  apellidos: string
  correo: string
  telefono: string
  cargo: string
  empresa?: string
  experiencia?: string | number
  estado: 'pendiente' | 'califica' | 'no_califica' | 'Pendiente'
  estado_ai?: string
  processing_status: 'uploading' | 'processing' | 'completed' | 'error'
  match?: number
  match_min: number
  fecha_registro: string
  cv_s3_key?: string
  cv_filename?: string
  cv_upload_date?: string
  last_updated?: string
  last_processed?: string
  processing_info: ProcessingInfo
  extracted_info?: ExtractedInfo
  cv_info: CVInfo
  ai_evaluation?: AIEvaluationResult
  // Real data structure fields
  evaluacion_ai?: AIEvaluationItem[]
  extracted_data?: {
    anos_experiencia?: number
    extraction_status: 'success' | 'partial' | 'error'
    extraction_errors: string[]
    correo?: string
    cargo_actual?: string
    telefono?: string
    nombres?: string
  }
  url_visor?: string
  accion: {
    nombre: string
    desc: string
    enlace: boolean
  }
  // Qualification fields
  qualification_status: 'pendiente' | 'califica' | 'no_califica'
  qualified_by?: string
  qualified_at?: string
  qualification_notes?: string
}

export interface CandidatesParams extends PaginationParams {
  id_solicitud: string
}

export interface CandidatesResponse {
  data: {
    total_items: number
    items: Candidate[]
    total_pages: number
    next_key?: string
    page_size: number
    solicitud: unknown
    processing_summary: ProcessingSummary
    filters: {
      estado?: string
      processing_status?: string
      qualification_status?: string
      sort_field: string
      sort_order: string
    }
  }
}

export interface CandidateDetailResponse {
  data: Candidate
}

// File upload types
export interface UploadFileRequest {
  filename: string
  content_type: string
  size: number
}

export interface PresignedUrlResponse {
  filename: string
  upload_url: string
  fields: { [key: string]: string }
  s3_key: string
  expires_at: string
}

export interface UploadUrlsResponse {
  upload_urls: PresignedUrlResponse[]
}

export interface UploadCompletionRequest {
  uploads: Array<{
    s3_key: string
    filename: string
  }>
}

export interface CreatedPostulacion {
  id_postulacion: string
  s3_key: string
  filename: string
  processing_status: string
  created_at: string
}

export interface PostulacionCreationResponse {
  message: string
  postulaciones: CreatedPostulacion[]
}

export interface UploadProgress {
  filename: string
  progress: number
  status: 'pending' | 'uploading' | 'completed' | 'error'
  error?: string
}

export interface UploadResult {
  progress: UploadProgress[]
  completed?: PostulacionCreationResponse
}

// CV viewing types
export interface CVUrlResponse {
  data: {
    cv_url: string
    expires_at: string
    filename?: string
  }
}

// Status update types
export interface StatusUpdate {
  id_postulacion: string
  estado: 'califica' | 'no_califica' | 'pendiente'
}

export interface StatusUpdateRequest {
  updates: StatusUpdate[]
}

// Email notification types
export interface EmailRequest {
  send_emails: boolean
  message?: string
}

export interface EmailResponse {
  message: string
  emails_sent?: number
  success: boolean
}

// Qualification types
export interface QualificationUpdateRequest {
  qualification_status: 'califica' | 'no_califica'
  notes?: string
}

export interface QualificationUpdateResponse {
  success: boolean
  data: {
    id_postulacion: string
    qualification_status: string
    qualified_by: string
    qualified_at: string
  }
}

export interface BulkQualificationRequest {
  candidate_ids: string[]
  qualification_status: 'califica' | 'no_califica'
  notes?: string
}

export interface BulkQualificationResponse {
  success: boolean
  data: {
    updated_count: number
    failed_updates: string[]
    updated_candidates: Array<{
      id_postulacion: string
      qualification_status: string
    }>
  }
}

// Form types for job posting creation
export interface JobPostingFormData {
  puesto: string
  funciones: Array<{
    funcion: string
    ocupabilidad: number
  }>
  idiomas: Array<{
    lenguaje: string
    nivel: string
  }>
  experiencia_puesto: {
    anios: number
    meses: number
  }
  experiencia_area: {
    anios: number
    meses: number
  }
  estudios: Array<{
    nivel: string
    detalle: string
  }>
  conocimientos: string[]
  requisitos_indispensables: string[]
  solicitante: {
    nombre: string
    correo: string
    cargo: string
  }
}

// Validation types
export interface ValidationResult {
  isValid: boolean
  errors: string[]
}

// HTTP Client configuration types
export interface RequestConfig {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  url: string
  data?: unknown
  params?: Record<string, string | number | boolean>
  headers?: Record<string, string>
  timeout?: number
}

export interface RetryConfig {
  maxRetries: number
  retryDelay: number
  retryCondition?: (error: ApiError) => boolean
}

// User Management Types
export interface UserInfo {
  id: string
  email: string
  name: string
  role: string
  status: 'active' | 'inactive' | 'pending_confirmation'
  created_at: string
  last_login?: string
  created_by?: string
  cognito_username?: string // Optional since API returns id instead
  cognito_groups?: string[]
  // Additional fields from actual API response
  cognito_status?: string
  enabled?: boolean
  groups?: string[]
  last_modified?: string
  email_verified?: boolean
}

export interface CreateUserRequest {
  email: string
  name: string
  role: string
  temporary_password?: string
}

export interface UpdateUserRequest {
  name?: string
  role?: string
  status?: 'active' | 'inactive'
}

export interface GetUsersResponse {
  data: {
    items: UserInfo[]
    total_items: number
    next_key?: string
    page_size: number
  }
}

// Role Management Types
export interface Permission {
  resource: string
  actions: string[]
  conditions?: Record<string, any>
}

export interface RoleInfo {
  id: string
  name: string
  description: string
  permissions: Permission[]
  is_system_role: boolean
  user_count: number
  created_at: string
  updated_at: string
  cognito_group_name: string
}

export interface CreateRoleRequest {
  name: string
  description: string
  permissions: string[]
  is_system_role?: boolean
}

export interface UpdateRoleRequest {
  description?: string
  permissions?: string[]
}

export interface GetRolesResponse {
  data: {
    items: RoleInfo[]
  }
}

// Permission System Types - imported from separate enums file to avoid circular imports
export {
  UserRole,
  PermissionResource,
  PermissionAction,
  UserManagementError,
  JobDefinitionError as JobDefinitionErrorEnum,
  QualificationStatus,
} from './enums'

export interface PermissionMatrix {
  users: {
    create: boolean
    read: boolean
    update: boolean
    delete: boolean
  }
  roles: {
    create: boolean
    read: boolean
    update: boolean
    delete: boolean
  }
  solicitudes: {
    create: boolean
    read: boolean
    update: boolean
    delete: boolean
  }
  postulaciones: {
    read: boolean
    evaluate: boolean
    update_status: boolean
    qualify: boolean
    bulk_qualify: boolean
  }
  reports: {
    view: boolean
    export: boolean
  }
}

export interface UserPermissions {
  role: UserRole
  permissions: PermissionMatrix
  canManageUsers: () => boolean
  canManageRoles: () => boolean
  canCreateSolicitudes: () => boolean
  canEvaluatePostulaciones: () => boolean
  canViewReports: () => boolean
  canExportReports: () => boolean
  canQualifyCandidates: () => boolean
  canBulkQualifyCandidates: () => boolean
  hasPermission: (
    resource: PermissionResource,
    action: PermissionAction
  ) => boolean
}

// Default Permission Configurations
export const DEFAULT_PERMISSIONS: Record<UserRole, PermissionMatrix> = {
  [UserRole.ADMINISTRADOR]: {
    users: { create: true, read: true, update: true, delete: true },
    roles: { create: true, read: true, update: true, delete: true },
    solicitudes: { create: true, read: true, update: true, delete: true },
    postulaciones: {
      read: true,
      evaluate: true,
      update_status: true,
      qualify: true,
      bulk_qualify: true,
    },
    reports: { view: true, export: true },
  },
  [UserRole.EDITOR]: {
    users: { create: false, read: true, update: false, delete: false },
    roles: { create: false, read: true, update: false, delete: false },
    solicitudes: { create: true, read: true, update: true, delete: false },
    postulaciones: {
      read: true,
      evaluate: true,
      update_status: true,
      qualify: true,
      bulk_qualify: true,
    },
    reports: { view: true, export: false },
  },
  [UserRole.VISUALIZADOR]: {
    users: { create: false, read: false, update: false, delete: false },
    roles: { create: false, read: false, update: false, delete: false },
    solicitudes: { create: true, read: true, update: false, delete: false },
    postulaciones: {
      read: true,
      evaluate: false,
      update_status: false,
      qualify: false,
      bulk_qualify: false,
    },
    reports: { view: true, export: false },
  },
}

// User Management Form Types
export interface UserFormData {
  email: string
  name: string
  role: UserRole
  temporary_password?: string
  send_invitation?: boolean
  metadata?: {
    department?: string
    position?: string
    phone?: string
  }
}

export interface RoleFormData {
  name: string
  description: string
  permissions: PermissionMatrix
  is_system_role?: boolean
}

// User Management API Response Types
export interface UserManagementApiResponse<T = unknown> extends ApiResponse<T> {
  error_code?: UserManagementError
}

// User Management Context Types
export interface UserManagementContextType {
  users: UserInfo[]
  roles: RoleInfo[]
  loading: boolean
  error: string | null
  currentUser: UserInfo | null
  userPermissions: UserPermissions | null

  // User operations
  getUsers: (params?: PaginationParams) => Promise<GetUsersResponse>
  createUser: (userData: CreateUserRequest) => Promise<UserInfo>
  updateUser: (userId: string, userData: UpdateUserRequest) => Promise<UserInfo>
  deleteUser: (userId: string) => Promise<void>

  // Role operations
  getRoles: () => Promise<GetRolesResponse>
  createRole: (roleData: CreateRoleRequest) => Promise<RoleInfo>
  updateRole: (roleId: string, roleData: UpdateRoleRequest) => Promise<RoleInfo>
  deleteRole: (roleId: string) => Promise<void>

  // Permission checking
  checkPermission: (
    resource: PermissionResource,
    action: PermissionAction
  ) => boolean
  hasRole: (role: UserRole) => boolean

  // Utility functions
  refreshUserData: () => Promise<void>
  clearError: () => void
}

// Workflow System Types
export interface WorkflowLogic {
  type: 'AND' | 'OR' | 'USER' | 'ROLE'
  conditions?: WorkflowLogic[]
  user_id?: string
  role_name?: string
  required_count?: number // For "at least N approvals" scenarios
}

export interface WorkflowConfiguration {
  id: string
  name: string
  description?: string
  is_active: string // Changed to string type for DynamoDB GSI compatibility ("true"/"false")
  created_by: string
  created_at: string
  updated_at: string
  workflow_logic: WorkflowLogic
}

export interface ApprovalRecord {
  user_id: string
  user_name: string
  user_email: string
  status: 'pending' | 'approved' | 'rejected'
  timestamp?: string
  comments?: string
}

export interface SolicitudWorkflowState {
  id_solicitud: string
  workflow_id?: string
  workflow_logic?: WorkflowLogic
  approval_status: 'pending' | 'approved' | 'rejected'
  approvals: ApprovalRecord[]
  created_at: string
  updated_at: string
  completed_at?: string
}

export interface CreateWorkflowRequest {
  name: string
  description?: string
  workflow_logic: WorkflowLogic
}

export interface UpdateWorkflowRequest {
  name?: string
  description?: string
  workflow_logic?: WorkflowLogic
  is_active?: string // Changed to string type ("true"/"false")
}

export interface GetWorkflowsResponse {
  data: {
    workflows: WorkflowConfiguration[]
    total_count: number
    next_key?: string
  }
}

export interface WorkflowValidationResult {
  isValid: boolean
  errors: string[]
  warnings?: string[]
}

// Workflow Builder UI Types
export interface WorkflowNode {
  id: string
  type: 'AND' | 'OR' | 'USER' | 'ROLE'
  user_id?: string
  user_name?: string
  user_email?: string
  role_name?: string
  required_count?: number
  children?: WorkflowNode[]
  parent_id?: string
}

// Upload Permission Types
export interface UploadPermissionResponse {
  upload_allowed: boolean
  reason?: string
  approval_status: 'pending' | 'approved' | 'rejected'
  workflow_info?: {
    has_workflow: boolean
    workflow_id?: string
    approval_status: 'pending' | 'approved' | 'rejected'
    pending_approvals: number
    total_approvals: number
    completion_percentage: number
  }
}

export interface WorkflowBuilderState {
  nodes: WorkflowNode[]
  selectedNode: string | null
  draggedNode: WorkflowNode | null
  isValid: boolean
  errors: string[]
}

// Requirement Creation System Types
export interface RequirementFieldConfig {
  field_id: string
  field_name: string
  field_type: 'textbox' | 'checkbox' | 'dropdown'
  is_required: boolean
  dropdown_options?: string[]
  display_order: number
  created_at: string
  updated_at: string
}

export interface RequirementFormData {
  puesto_solicitado: string
  [key: string]: any // Dynamic fields based on configuration
}

export interface RequirementFieldsResponse {
  data: {
    fields: RequirementFieldConfig[]
    cached: boolean
    total_fields: number
  }
}

export interface CreateRequirementRequest {
  puesto_solicitado: string
  [key: string]: any // Dynamic fields
}

export interface CreateRequirementResponse {
  data: {
    id_solicitud: string
    requirement_status: string
    approval_status: string
    requires_approval: boolean
    job_definition_enabled: boolean
    fecha_creacion: string
    message: string
    workflow_assigned: boolean
  }
}

// Job Definition Management Types
export interface JobDefinition {
  id: string
  name: string
  puesto: string
  funciones: Array<{
    funcion: string
    ocupabilidad: number
  }>
  perfil: {
    idiomas: Array<{
      lenguaje: string
      nivel: string
    }>
    estudios: Array<{
      nivel: string
      detalle: string
    }>
    conocimientos: string[]
  }
  requisitos: {
    indispensables: string[]
  }
  created_by: string
  created_at: string
  updated_at: string
  is_active: boolean
  version: number
}

export interface JobDefinitionFormData {
  name: string
  puesto: string
  funciones: Array<{
    funcion: string
    ocupabilidad: number
  }>
  idiomas: Array<{
    lenguaje: string
    nivel: string
  }>
  estudios: Array<{
    nivel: string
    detalle: string
  }>
  conocimientos: string[]
  requisitos_indispensables: string[]
}

export interface CreateJobDefinitionRequest {
  name: string
  puesto: string
  funciones: Array<{
    funcion: string
    ocupabilidad: number
  }>
  perfil: {
    idiomas: Array<{
      lenguaje: string
      nivel: string
    }>
    estudios: Array<{
      nivel: string
      detalle: string
    }>
    conocimientos: string[]
  }
  requisitos: {
    indispensables: string[]
  }
}

export interface UpdateJobDefinitionRequest extends CreateJobDefinitionRequest {
  version: number
}

export interface JobDefinitionListResponse {
  data: {
    items: JobDefinition[]
    total_items: number
    next_key?: string
    page_size: number
  }
}

export interface JobDefinitionResponse {
  data: JobDefinition
}

export interface JobDefinitionSelectorOption {
  value: string
  label: string
  puesto: string
}

export interface JobDefinitionError {
  code: string
  message: string
  field?: string
  details?: any
}

export interface JobDefinitionApiResponse<T = unknown> extends ApiResponse<T> {
  error_code?: JobDefinitionErrorEnum
}

// Google Forms Integration Types
export interface IntegrationConfiguration {
  integration_id: string
  integration_type: 'google_forms'
  service_account_credentials_arn?: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface CreateIntegrationConfigRequest {
  integration_type: 'google_forms'
  service_account_credentials: string // JSON string of service account credentials
}

export interface UpdateIntegrationConfigRequest {
  service_account_credentials?: string
  is_active?: boolean
}

export interface IntegrationConfigResponse {
  data: IntegrationConfiguration
}

export interface JobIntegration {
  id_solicitud: string
  integration_type: 'google_forms'
  spreadsheet_url: string
  cv_column_index?: number
  last_sync_timestamp?: string
  sync_status: 'active' | 'paused' | 'error'
  error_details?: string
  created_at: string
  updated_at: string
}

export interface CreateJobIntegrationRequest {
  integration_type: 'google_forms'
  spreadsheet_url: string
}

export interface UpdateJobIntegrationRequest {
  spreadsheet_url?: string
  sync_status?: 'active' | 'paused' | 'error'
}

export interface JobIntegrationResponse {
  data: JobIntegration
}

export interface IntegrationStatusResponse {
  data: {
    integration_type: 'google_forms'
    is_configured: boolean
    is_active: boolean
    total_job_integrations: number
    active_job_integrations: number
    last_sync_timestamp?: string
    sync_metrics: {
      success_rate: number
      total_syncs: number
      failed_syncs: number
      avg_processing_time: number
    }
  }
}

// Re-export utility functions and constants
export * from '../utils/permissions'
export * from '../utils/userManagementValidation'
export * from '../utils/jobDefinitionValidation'
export * from '../utils/solicitudValidation'
export * from '../constants/userManagement'
